<template>
    <div class="equip-status-monitor-setting">
        <div class="setting-content">
            <!-- 1. 설비 유형 선택 -->
            <div class="setting-section">
                <h5>{{ $t("설비 선택") }}</h5>

                <div class="form-group">
                    <label>{{ $t("설비 유형") }}</label>
                    <select v-model="selectedEquipType" class="form-control" @change="onEquipTypeChange">
                        <option :value="null">{{ $t("설비 유형을 선택하세요") }}</option>
                        <option v-for="type in equipTypeList" :key="type.equipType" :value="type.equipType">
                            {{ type.equipTypeName }}
                        </option>
                    </select>
                </div>

                <!-- 2. 설비 목록 선택 -->
                <div v-if="selectedEquipType" class="form-group">
                    <label>{{ $t("표시할 설비 선택") }}</label>
                    <div class="equip-selection-list">
                        <div v-for="equip in availableEquips" :key="equip.equipIdx" class="equip-selection-item">
                            <div class="equip-checkbox-header">
                                <input
                                    type="checkbox"
                                    :value="equip.equipIdx"
                                    :checked="isEquipSelected(equip.equipIdx)"
                                    @change="toggleEquipSelection(equip)"
                                />
                                <strong>{{ equip.equipName }}</strong>
                                <span class="equip-module">{{ equip.moduleName }}</span>
                            </div>

                            <!-- 선택된 설비의 속성 선택 -->
                            <div v-if="isEquipSelected(equip.equipIdx)" class="prop-selection-area">
                                <label class="prop-selection-label">{{ $t("표시할 속성 선택") }}</label>

                                <!-- 속성 검색 필터 및 전체 선택/해제 -->
                                <div class="prop-filter-row">
                                    <div class="prop-filter">
                                        <i class="fas fa-search"></i>
                                        <input
                                            type="text"
                                            v-model="propFilterText[equip.equipIdx]"
                                            :placeholder="$t('속성명 검색')"
                                            class="prop-filter-input"
                                        />
                                        <button
                                            v-if="propFilterText[equip.equipIdx]"
                                            class="clear-prop-filter"
                                            @click="$set(propFilterText, equip.equipIdx, '')"
                                            :title="$t('검색어 지우기')"
                                        >
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </div>
                                    <div class="prop-actions">
                                        <button
                                            class="btn-prop-action"
                                            @click="selectAllProps(equip.equipIdx)"
                                            :title="$t('전체 선택')"
                                        >
                                            <i class="fas fa-check-double"></i>
                                            {{ $t("전체 선택") }}
                                        </button>
                                        <button
                                            class="btn-prop-action"
                                            @click="deselectAllProps(equip.equipIdx)"
                                            :title="$t('전체 해제')"
                                        >
                                            <i class="fas fa-times-circle"></i>
                                            {{ $t("전체 해제") }}
                                        </button>
                                    </div>
                                </div>

                                <div class="prop-checkbox-list">
                                    <div
                                        v-if="getFilteredEquipProps(equip.equipIdx).length === 0"
                                        class="no-props-message"
                                    >
                                        <i class="fas fa-info-circle"></i>
                                        {{ $t("검색 결과가 없습니다.") }}
                                    </div>
                                    <label
                                        v-for="prop in getFilteredEquipProps(equip.equipIdx)"
                                        :key="prop.propCode"
                                        class="prop-checkbox-item"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="prop.propCode"
                                            v-model="currentEquipPropsSelection[equip.equipIdx]"
                                        />
                                        <span>{{ prop.propName }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button class="btn btn-add" @click="addEquipTypeSelection" :disabled="!hasValidSelection()">
                        <i class="fas fa-plus"></i>
                        {{ $t("추가") }}
                    </button>
                </div>
            </div>

            <!-- 3. 선택된 설비 목록 -->
            <div v-if="equipSelections.length > 0" class="setting-section">
                <h5>{{ $t("선택된 설비 목록") }}</h5>
                <div class="selected-list">
                    <div v-for="(selection, sIndex) in equipSelections" :key="sIndex" class="selection-item">
                        <div class="selection-info">
                            <app-icon :icon="getEquipTypeIcon(selection.equipType)" class="selection-icon" />
                            <div class="selection-details">
                                <strong>{{ selection.equipTypeName }}</strong>
                                <div class="equip-props-summary">
                                    <div
                                        v-for="(equip, eIndex) in selection.equipList"
                                        :key="equip.equipIdx"
                                        class="equip-summary-item"
                                    >
                                        <span class="equip-summary-name">{{ equip.equipName }}</span>
                                        <span class="prop-count-badge"> {{ equip.selectedProps.length }}개 속성 </span>
                                        <div class="equip-item-actions">
                                            <button
                                                class="btn-equip-action btn-equip-edit"
                                                @click="openEquipEditModal(sIndex, eIndex, equip)"
                                                :title="$t('속성 편집')"
                                            >
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button
                                                class="btn-equip-action btn-equip-remove"
                                                @click="removeEquipFromSelection(sIndex, eIndex)"
                                                :title="$t('설비 삭제')"
                                            >
                                                <i class="fas fa-times"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button class="btn-remove" @click="removeSelection(sIndex)" :title="$t('설비 유형 전체 삭제')">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- 설비 속성 편집 모달 -->
            <b-modal
                ref="equipEditModal"
                :title="$t('설비 속성 편집')"
                body-class="p-t-0 p-b-0"
                hide-footer
                size="lg"
                @hidden="onEquipEditModalHidden"
            >
                <div v-if="editingEquip" class="equip-edit-modal-content">
                    <div class="equip-edit-header">
                        <h6>{{ editingEquip.equipName }}</h6>
                        <span class="equip-type-badge">{{ getEditingEquipTypeName() }}</span>
                    </div>

                    <!-- 속성 검색 필터 및 전체 선택/해제 -->
                    <div class="prop-filter-row">
                        <div class="prop-filter">
                            <i class="fas fa-search"></i>
                            <input
                                type="text"
                                v-model="editingPropFilterText"
                                :placeholder="$t('속성명 검색')"
                                class="prop-filter-input"
                            />
                            <button
                                v-if="editingPropFilterText"
                                class="clear-prop-filter"
                                @click="editingPropFilterText = ''"
                                :title="$t('검색어 지우기')"
                            >
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <div class="prop-actions">
                            <button class="btn-prop-action" @click="selectAllEditingProps" :title="$t('전체 선택')">
                                <i class="fas fa-check-double"></i>
                                {{ $t("전체 선택") }}
                            </button>
                            <button class="btn-prop-action" @click="deselectAllEditingProps" :title="$t('전체 해제')">
                                <i class="fas fa-times-circle"></i>
                                {{ $t("전체 해제") }}
                            </button>
                        </div>
                    </div>

                    <!-- 속성 체크박스 리스트 -->
                    <div class="prop-checkbox-list">
                        <div v-if="getFilteredEditingProps().length === 0" class="no-props-message">
                            <i class="fas fa-info-circle"></i>
                            {{ $t("검색 결과가 없습니다.") }}
                        </div>
                        <label
                            v-for="prop in getFilteredEditingProps()"
                            :key="prop.propCode"
                            class="prop-checkbox-item"
                        >
                            <input type="checkbox" :value="prop.propCode" v-model="editingSelectedProps" />
                            <span>{{ prop.propName }}</span>
                        </label>
                    </div>

                    <!-- 저장/취소 버튼 -->
                    <div class="equip-edit-actions">
                        <button class="btn btn-secondary" @click="cancelEquipEdit">
                            {{ $t("취소") }}
                        </button>
                        <button class="btn btn-primary" @click="saveEquipEdit">
                            {{ $t("저장") }}
                        </button>
                    </div>
                </div>
            </b-modal>

            <!-- 4. 배경색 선택 -->
            <div class="setting-section">
                <h5>{{ $t("위젯 스타일") }}</h5>
                <div class="form-group">
                    <label>{{ $t("배경색") }}</label>
                    <EnergyColorPalette v-model="widgetBackgroundColor" :energyType="'general'" />
                </div>
            </div>
        </div>

        <!-- 버튼 영역 -->
        <div class="setting-actions">
            <button class="btn btn-secondary" @click="$emit('on-modal-cancel')">
                {{ $t("취소") }}
            </button>
            <button class="btn btn-primary" @click="onSave" :disabled="equipSelections.length === 0 || isLoading">
                <span v-if="isLoading">
                    <i class="fas fa-spinner fa-spin"></i>
                    {{ $t("저장 중...") }}
                </span>
                <span v-else>
                    {{ $t("저장") }}
                </span>
            </button>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import EnergyColorPalette from "@components/colorpalette/EnergyColorPalette.vue";
import { getEquipTypeIconFromStore } from "@src/utils/iconDisplay.js";

export default {
    name: "EquipStatusMonitorSetting",
    components: {
        EnergyColorPalette,
    },
    props: {
        initSetting: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            equipTypeList: [],
            selectedEquipType: null,
            availableEquips: [],

            // 현재 유형에서 선택 중인 설비와 속성
            currentEquipSelection: [], // 선택된 equipIdx 배열
            currentEquipPropsSelection: {}, // { equipIdx: [propCode1, propCode2, ...] }
            equipPropsData: {}, // { equipIdx: [ioProps] } - 각 설비의 ioProps
            propFilterText: {}, // { equipIdx: '검색어' } - 각 설비의 속성 필터 검색어

            equipSelections: [],

            // 조회한 설비 데이터 저장
            equipStatusData: {},
            equipTypeInfoMap: {},

            widgetBackgroundColor: "",
            isLoading: false,

            // 설비 편집 관련
            editingEquip: null, // { selectionIndex, equipIndex, equipIdx, equipName, equipType }
            editingSelectedProps: [], // 편집 중인 속성 코드 배열
            editingEquipProps: [], // 편집 중인 설비의 ioProps
            editingPropFilterText: "", // 편집 중인 설비의 속성 필터
        };
    },
    mounted() {
        this.initData();
        this.loadEquipTypeList();
    },
    methods: {
        // Toast 헬퍼 메서드
        showToast(message, variant = "info") {
            if (this.$bvToast) {
                this.$bvToast.toast(message, {
                    variant: variant,
                    solid: true,
                    autoHideDelay: 3000,
                });
            } else if (this.$toast) {
                if (variant === "warning" && this.$toast.warning) {
                    this.$toast.warning(message);
                } else if ((variant === "error" || variant === "danger") && this.$toast.error) {
                    this.$toast.error(message);
                } else if (variant === "success" && this.$toast.success) {
                    this.$toast.success(message);
                } else if (variant === "info" && this.$toast.info) {
                    this.$toast.info(message);
                }
            }
        },

        // 초기화
        initData() {
            if (this.initSetting?.selectedEquips) {
                this.equipSelections = JSON.parse(JSON.stringify(this.initSetting.selectedEquips));
            }
            if (this.initSetting?.panel?.backgroundColor) {
                this.widgetBackgroundColor = this.initSetting.panel.backgroundColor;
            }
            // 기존 데이터 복원 (설정 모달 재오픈 시 필요한 정보)
            if (this.initSetting?.equipStatusData) {
                this.equipStatusData = JSON.parse(JSON.stringify(this.initSetting.equipStatusData));
            }
            if (this.initSetting?.equipTypeInfoMap) {
                this.equipTypeInfoMap = JSON.parse(JSON.stringify(this.initSetting.equipTypeInfoMap));
            }
        },

        // 설비 유형 목록 조회
        async loadEquipTypeList() {
            try {
                const result = await backEndApi.equipTypes.getEquipTypeList("Y");
                if (result.status === 200) {
                    this.equipTypeList = result.data;
                }
            } catch (error) {
                console.error("Error loading equip type list:", error);
                this.showToast(this.$t("설비 유형 목록을 가져오는 데 실패했습니다."), "error");
            }
        },

        // 설비 유형 선택 시
        async onEquipTypeChange() {
            this.availableEquips = [];
            this.currentEquipSelection = [];
            this.currentEquipPropsSelection = {};
            this.equipPropsData = {};
            this.propFilterText = {};

            if (!this.selectedEquipType) return;

            try {
                // 먼저 설비 유형 정보 조회 (dispLevel 확인용)
                const typeResult = await backEndApi.equipTypes.getEquipType(this.selectedEquipType, "Y");
                if (typeResult.status === 200 && typeResult.data) {
                    this.$set(this.equipTypeInfoMap, this.selectedEquipType, typeResult.data);
                }

                // 설비 목록 조회
                const result = await backEndApi.equips.getEquipList(this.selectedEquipType, null, null);

                if (result.status === 200 && result.data) {
                    this.availableEquips = result.data.filter((e) => e.enabled === "Y");
                }
            } catch (error) {
                console.error("Error loading equip list:", error);
                this.showToast(this.$t("설비 목록을 가져오는 데 실패했습니다."), "error");
            }
        },

        // 설비 체크박스 토글
        async toggleEquipSelection(equip) {
            const equipIdx = equip.equipIdx;
            const index = this.currentEquipSelection.indexOf(equipIdx);

            if (index > -1) {
                // 선택 해제
                this.currentEquipSelection.splice(index, 1);
                this.$delete(this.currentEquipPropsSelection, equipIdx);
                this.$delete(this.equipPropsData, equipIdx);
                this.$delete(this.propFilterText, equipIdx);
            } else {
                // 선택 추가 - ioProps 조회
                this.currentEquipSelection.push(equipIdx);
                this.$set(this.currentEquipPropsSelection, equipIdx, []);
                this.$set(this.propFilterText, equipIdx, "");

                // 설비 상세 조회하여 ioProps 가져오기
                await this.loadEquipProps(equipIdx);
            }
        },

        // 설비의 ioProps 조회
        async loadEquipProps(equipIdx) {
            try {
                const result = await backEndApi.equips.getEquipDetailList(equipIdx, "Y");
                if (result.status === 200 && result.data && result.data.ioProps) {
                    this.$set(this.equipPropsData, equipIdx, result.data.ioProps);
                    // 기본적으로 아무것도 선택하지 않음
                    // this.$set(this.currentEquipPropsSelection, equipIdx, []) 는 이미 toggleEquipSelection에서 설정됨
                }
            } catch (error) {
                console.error("Error loading equip props:", error);
                this.showToast(this.$t("설비 속성을 가져오는 데 실패했습니다."), "error");
            }
        },

        // 설비가 선택되었는지 확인
        isEquipSelected(equipIdx) {
            return this.currentEquipSelection.includes(equipIdx);
        },

        // 설비의 ioProps 반환
        getEquipProps(equipIdx) {
            return this.equipPropsData[equipIdx] || [];
        },

        // 필터링된 설비 속성 반환
        getFilteredEquipProps(equipIdx) {
            const props = this.equipPropsData[equipIdx] || [];
            const filterText = this.propFilterText[equipIdx];

            if (!filterText) {
                return props;
            }

            const searchText = filterText.toLowerCase();
            return props.filter(
                (prop) =>
                    prop.propName?.toLowerCase().includes(searchText) ||
                    prop.propCode?.toLowerCase().includes(searchText)
            );
        },

        // 전체 속성 선택
        selectAllProps(equipIdx) {
            const filteredProps = this.getFilteredEquipProps(equipIdx);
            const allPropCodes = filteredProps.map((prop) => prop.propCode);
            this.$set(this.currentEquipPropsSelection, equipIdx, allPropCodes);
        },

        // 전체 속성 해제
        deselectAllProps(equipIdx) {
            this.$set(this.currentEquipPropsSelection, equipIdx, []);
        },

        // 유효한 선택인지 확인
        hasValidSelection() {
            if (this.currentEquipSelection.length === 0) return false;

            // 선택된 설비 중 최소 1개의 속성이 선택되었는지 확인
            return this.currentEquipSelection.some((equipIdx) => {
                const props = this.currentEquipPropsSelection[equipIdx];
                return props && props.length > 0;
            });
        },

        // 선택 추가
        addEquipTypeSelection() {
            if (!this.hasValidSelection()) return;

            const selectedType = this.equipTypeList.find((t) => t.equipType === this.selectedEquipType);

            if (!selectedType) {
                console.error("Selected equip type not found in equipTypeList");
                this.showToast(this.$t("설비 유형 정보를 찾을 수 없습니다."), "error");
                return;
            }

            // 동일한 설비 유형이 이미 존재하는지 확인
            const existingSelection = this.equipSelections.find((s) => s.equipType === this.selectedEquipType);

            if (existingSelection) {
                // 기존 그룹에 설비 추가 (중복 제외)
                const existingEquipIdxList = existingSelection.equipList.map((e) => e.equipIdx);

                // 새로 추가할 설비만 필터링 (이미 선택된 설비 제외)
                const newEquipList = this.currentEquipSelection
                    .filter((equipIdx) => !existingEquipIdxList.includes(equipIdx))
                    .map((equipIdx) => ({
                        equipIdx,
                        equipName: this.availableEquips.find((e) => e.equipIdx === equipIdx)?.equipName,
                        selectedProps: [...this.currentEquipPropsSelection[equipIdx]],
                    }));

                if (newEquipList.length === 0) {
                    // 추가할 새로운 설비가 없음 (모두 이미 선택됨)
                    this.showToast(this.$t("선택한 설비는 이미 추가되어 있습니다."), "warning");
                    // 초기화
                    this.selectedEquipType = null;
                    this.currentEquipSelection = [];
                    this.currentEquipPropsSelection = {};
                    this.availableEquips = [];
                    this.equipPropsData = {};
                    this.propFilterText = {};
                    return;
                }

                // 기존 그룹에 새로운 설비 추가
                existingSelection.equipList.push(...newEquipList);
                this.showToast(this.$t("설비를 추가했습니다."), "success");
            } else {
                // 새로운 설비 유형 그룹 생성
                const equipList = this.currentEquipSelection.map((equipIdx) => ({
                    equipIdx,
                    equipName: this.availableEquips.find((e) => e.equipIdx === equipIdx)?.equipName,
                    selectedProps: [...this.currentEquipPropsSelection[equipIdx]],
                }));

                this.equipSelections.push({
                    equipType: this.selectedEquipType,
                    equipTypeName: selectedType.equipTypeName,
                    equipList,
                });
            }

            // 초기화
            this.selectedEquipType = null;
            this.currentEquipSelection = [];
            this.currentEquipPropsSelection = {};
            this.availableEquips = [];
            this.equipPropsData = {};
            this.propFilterText = {};
        },

        // 선택 제거 (설비 유형 전체 삭제)
        removeSelection(index) {
            this.equipSelections.splice(index, 1);
        },

        // 개별 설비 삭제
        removeEquipFromSelection(selectionIndex, equipIndex) {
            const selection = this.equipSelections[selectionIndex];
            if (!selection || !selection.equipList[equipIndex]) return;

            const equipName = selection.equipList[equipIndex].equipName;
            selection.equipList.splice(equipIndex, 1);

            // 설비 목록이 비어있으면 전체 그룹 삭제
            if (selection.equipList.length === 0) {
                this.equipSelections.splice(selectionIndex, 1);
            }

            this.showToast(`${equipName} ${this.$t("설비가 삭제되었습니다.")}`, "success");
        },

        // 설비 속성 편집 모달 열기
        async openEquipEditModal(selectionIndex, equipIndex, equip) {
            const selection = this.equipSelections[selectionIndex];
            if (!selection) return;

            this.editingEquip = {
                selectionIndex,
                equipIndex,
                equipIdx: equip.equipIdx,
                equipName: equip.equipName,
                equipType: selection.equipType,
            };

            // 현재 선택된 속성 복사
            this.editingSelectedProps = [...equip.selectedProps];
            this.editingPropFilterText = "";

            // 설비의 ioProps가 없으면 로드
            if (!this.equipPropsData[equip.equipIdx]) {
                await this.loadEquipProps(equip.equipIdx);
            }

            this.editingEquipProps = this.equipPropsData[equip.equipIdx] || [];

            // 모달 열기
            this.$refs.equipEditModal.show();
        },

        // 편집 중인 설비의 설비 유형 이름 가져오기
        getEditingEquipTypeName() {
            if (!this.editingEquip) return "";
            const selection = this.equipSelections[this.editingEquip.selectionIndex];
            return selection ? selection.equipTypeName : "";
        },

        // 편집 중인 설비의 필터링된 속성 반환
        getFilteredEditingProps() {
            if (!this.editingEquipProps || this.editingEquipProps.length === 0) return [];
            if (!this.editingPropFilterText) return this.editingEquipProps;

            const searchText = this.editingPropFilterText.toLowerCase();
            return this.editingEquipProps.filter(
                (prop) =>
                    prop.propName?.toLowerCase().includes(searchText) ||
                    prop.propCode?.toLowerCase().includes(searchText)
            );
        },

        // 편집 중인 설비의 전체 속성 선택
        selectAllEditingProps() {
            const filteredProps = this.getFilteredEditingProps();
            const allPropCodes = filteredProps.map((prop) => prop.propCode);
            // 중복 제거
            this.editingSelectedProps = [...new Set([...this.editingSelectedProps, ...allPropCodes])];
        },

        // 편집 중인 설비의 전체 속성 해제
        deselectAllEditingProps() {
            const filteredProps = this.getFilteredEditingProps();
            const filteredPropCodes = filteredProps.map((prop) => prop.propCode);
            this.editingSelectedProps = this.editingSelectedProps.filter((code) => !filteredPropCodes.includes(code));
        },

        // 설비 속성 편집 저장
        saveEquipEdit() {
            if (!this.editingEquip) return;

            const selection = this.equipSelections[this.editingEquip.selectionIndex];
            if (!selection || !selection.equipList[this.editingEquip.equipIndex]) return;

            // 속성 업데이트
            selection.equipList[this.editingEquip.equipIndex].selectedProps = [...this.editingSelectedProps];

            this.showToast(this.$t("설비 속성이 저장되었습니다."), "success");
            this.$refs.equipEditModal.hide();
        },

        // 설비 속성 편집 취소
        cancelEquipEdit() {
            this.$refs.equipEditModal.hide();
        },

        // 설비 편집 모달 닫힐 때
        onEquipEditModalHidden() {
            this.editingEquip = null;
            this.editingSelectedProps = [];
            this.editingEquipProps = [];
            this.editingPropFilterText = "";
        },

        // 선택된 모든 설비의 상태를 한 번에 조회
        async loadAllEquipStatus() {
            try {
                // 1. 선택된 모든 equipIdx 추출
                const allEquipIdxList = this.equipSelections.flatMap((sel) => sel.equipList.map((eq) => eq.equipIdx));

                // 2. 각 설비 상세 조회
                const promises = allEquipIdxList.map((equipIdx) => backEndApi.equips.getEquipDetailList(equipIdx, "Y"));

                const results = await Promise.all(promises);

                // 3. equipStatusData 맵 생성
                this.equipStatusData = {};
                results.forEach((result) => {
                    if (result.status === 200 && result.data) {
                        const data = result.data;
                        this.equipStatusData[data.equipIdx] = data;
                    }
                });

                // 4. 선택된 모든 설비 유형의 정보 조회 (formatPropValue에서 사용하기 위해)
                const equipTypes = [...new Set(this.equipSelections.map((sel) => sel.equipType))];
                const missingEquipTypes = equipTypes.filter((equipType) => !this.equipTypeInfoMap[equipType]);

                if (missingEquipTypes.length > 0) {
                    const equipTypePromises = missingEquipTypes.map((equipType) =>
                        backEndApi.equipTypes.getEquipType(equipType, "Y").then((result) => ({
                            equipType,
                            result,
                        }))
                    );

                    const equipTypeResults = await Promise.all(equipTypePromises);
                    equipTypeResults.forEach(({ equipType, result }) => {
                        if (result.status === 200 && result.data) {
                            this.$set(this.equipTypeInfoMap, equipType, result.data);
                        }
                    });
                }
            } catch (error) {
                console.error("Error loading equip status:", error);
                this.showToast(this.$t("설비 상태를 가져오는 데 실패했습니다."), "error");
                throw error;
            }
        },

        // 저장
        async onSave() {
            if (this.equipSelections.length === 0) {
                this.showToast(this.$t("최소 1개 이상의 설비를 선택해주세요."), "warning");
                return;
            }

            this.isLoading = true;

            try {
                // 저장 전에 선택된 모든 설비의 상태를 조회
                await this.loadAllEquipStatus();

                const setting = {
                    selectedEquips: this.equipSelections,
                    equipStatusData: this.equipStatusData,
                    equipTypeInfoMap: this.equipTypeInfoMap,
                    panel: {
                        backgroundColor: this.widgetBackgroundColor,
                    },
                };

                this.$emit("on-modal-ok", setting);
            } catch (error) {
                console.error("Error on save:", error);
            } finally {
                this.isLoading = false;
            }
        },

        // 설비 유형 아이콘
        getEquipTypeIcon(equipType) {
            return getEquipTypeIconFromStore(this.$store, equipType);
        },
    },
};
</script>

<style scoped>
.equip-status-monitor-setting {
    padding: 20px;
}

.setting-content {
    margin-bottom: 20px;
}

.setting-section {
    margin-bottom: 30px;
    padding: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f8f9fa;
}

.setting-section h5 {
    margin-bottom: 20px;
    color: #333;
    font-weight: 600;
    border-bottom: 2px solid #007bff;
    padding-bottom: 8px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #555;
}

.form-control {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

/* 설비 선택 리스트 */
.equip-selection-list {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 12px;
    background: white;
    margin-bottom: 12px;
}

.equip-selection-item {
    margin-bottom: 16px;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.3s;
}

.equip-selection-item:last-child {
    margin-bottom: 0;
}

.equip-checkbox-header {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: background-color 0.2s;
}

.equip-checkbox-header:hover {
    background-color: #f0f0f0;
}

.equip-checkbox-header input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.equip-checkbox-header strong {
    color: #333;
    font-size: 14px;
    flex: 1;
}

.equip-module {
    color: #666;
    font-size: 12px;
    background: #e9ecef;
    padding: 2px 8px;
    border-radius: 10px;
}

/* 속성 선택 영역 */
.prop-selection-area {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
}

.prop-selection-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
}

/* 속성 필터 행 */
.prop-filter-row {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
    align-items: center;
}

/* 속성 필터 */
.prop-filter {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
}

.prop-filter i.fa-search {
    position: absolute;
    left: 10px;
    color: #999;
    font-size: 12px;
}

.prop-filter-input {
    width: 100%;
    padding: 8px 32px 8px 30px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 13px;
    transition: border-color 0.3s ease;
    background-color: #fff;
}

.prop-filter-input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
}

.clear-prop-filter {
    position: absolute;
    right: 6px;
    padding: 4px 6px;
    background: transparent;
    border: none;
    color: #999;
    cursor: pointer;
    border-radius: 3px;
    transition: all 0.2s;
    font-size: 11px;
}

.clear-prop-filter:hover {
    background: #f0f0f0;
    color: #666;
}

/* 속성 액션 버튼 */
.prop-actions {
    display: flex;
    gap: 6px;
}

.btn-prop-action {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;
    color: #555;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.btn-prop-action:hover {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
}

.btn-prop-action i {
    font-size: 11px;
}

.prop-checkbox-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
    min-height: 50px;
}

.no-props-message {
    grid-column: 1 / -1;
    padding: 20px;
    text-align: center;
    color: #999;
    font-size: 12px;
}

.no-props-message i {
    display: block;
    font-size: 20px;
    margin-bottom: 6px;
    opacity: 0.5;
}

.prop-checkbox-item {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    background: #f8f9fa;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 13px;
}

.prop-checkbox-item:hover {
    background-color: #e9ecef;
}

.prop-checkbox-item input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
}

/* 추가 버튼 */
.btn-add {
    width: 100%;
    padding: 10px;
    border: none;
    border-radius: 6px;
    background-color: #28a745;
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-add:hover:not(:disabled) {
    background-color: #218838;
}

.btn-add:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
}

/* 선택된 목록 */
.selected-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.selection-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    transition: all 0.3s;
}

.selection-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.selection-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.selection-icon {
    font-size: 24px;
    color: #007bff;
}

.selection-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.selection-details strong {
    color: #333;
    font-size: 14px;
    margin-bottom: 8px;
}

.equip-props-summary {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
}

.equip-summary-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    padding: 6px 8px;
    border-radius: 4px;
    background: #f8f9fa;
    margin-bottom: 4px;
    transition: background-color 0.2s;
}

.equip-summary-item:hover {
    background: #e9ecef;
}

.equip-summary-item:last-child {
    margin-bottom: 0;
}

.equip-summary-name {
    color: #555;
    font-weight: 500;
    flex: 1;
}

.prop-count-badge {
    display: inline-block;
    background: #007bff;
    color: white;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
}

.equip-item-actions {
    display: flex;
    gap: 4px;
    align-items: center;
}

.btn-equip-action {
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;
    color: #555;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}

.btn-equip-action:hover {
    transform: scale(1.1);
}

.btn-equip-edit:hover {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
}

.btn-equip-remove:hover {
    background-color: #dc3545;
    border-color: #dc3545;
    color: white;
}

.btn-remove {
    padding: 6px 10px;
    border: none;
    border-radius: 4px;
    background-color: #dc3545;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s;
}

.btn-remove:hover {
    background-color: #c82333;
}

/* 액션 버튼 */
.setting-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
}

.btn {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0056b3;
}

.btn-primary:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #545b62;
}

/* 설비 편집 모달 스타일 */
.equip-edit-modal-content {
    padding: 20px;
}

.equip-edit-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #e0e0e0;
}

.equip-edit-header h6 {
    margin: 0;
    color: #333;
    font-weight: 600;
    font-size: 16px;
}

.equip-type-badge {
    display: inline-block;
    background: #007bff;
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.equip-edit-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 20px;
    margin-top: 20px;
    border-top: 1px solid #e0e0e0;
}
</style>
